import { Droppable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import type { DropSlotProps } from './DropSlot';
import { useDragDropAnswerContext } from './DragDropAnswerContext';
import { OptionChip } from './OptionChip';
import { useChipClone } from '@/hooks/questions/dnd/useChipClone';
import { useSlotResizeFlip } from '@/hooks/questions/dnd/useSlotResizeFlip';

export function SingleDropSlot({
  id,
  inline = false,
  ariaLabel,
  className,
}: DropSlotProps) {
  const { tokensOf, disabled, onContainerClick, selectedId, flipFrom, flow } =
    useDragDropAnswerContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'questionView.dnd',
  });
  const tokens = tokensOf(id);
  const isEmpty = tokens.length === 0;
  const occupantId = tokens[0] ? String(tokens[0].id) : undefined;
  const renderClone = useChipClone();
  const occupantFlying = !!occupantId && !!flipFrom[occupantId];
  useSlotResizeFlip(id, occupantId, occupantFlying);

  return (
    <Droppable
      droppableId={id}
      direction={flow}
      isDropDisabled={disabled}
      renderClone={renderClone}
    >
      {(provided, snapshot) => {
        const isReplacing =
          !!occupantId &&
          snapshot.isDraggingOver &&
          snapshot.draggingOverWith !== occupantId;
        const showEmpty = isEmpty || !!snapshot.draggingFromThisWith;

        return (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            data-testid={`slot-${id}`}
            aria-label={ariaLabel ?? t('slotLabel')}
            aria-roledescription={t('dropZoneRoleDescription')}
            onClick={event => {
              if (event.defaultPrevented) return;
              onContainerClick(id);
            }}
            className={twMerge(
              'relative flex min-h-12 min-w-24 items-center rounded-lg p-1',
              inline ? 'inline-flex align-middle' : 'flex',
              flow === 'vertical' && 'flex-col',
              tokens.length > 0 ? 'min-w-auto' : '',
              showEmpty && 'border border-dashed border-secondary',
              selectedId && 'cursor-pointer',
              snapshot.isDraggingOver &&
                'border border-secondary bg-secondary/5',
              className,
            )}
          >
            {tokens.map((token, index) => (
              <OptionChip
                key={token.id}
                token={token}
                index={index}
                fadingOut={isReplacing}
              />
            ))}
            {occupantId === snapshot.draggingOverWith ||
            snapshot.draggingFromThisWith ? (
              provided.placeholder
            ) : (
              <span className="sr-only">{provided.placeholder}</span>
            )}
          </div>
        );
      }}
    </Droppable>
  );
}
